import { useState } from "react";
import { useDraftField, STATUS_WORDS } from "@/components/sales/draft/useDraftField";
import { DraftInput } from "@/components/sales/draft/DraftInput";
import { draftAct } from "@/components/sales/draft/draftApi";
import { UserName } from "@/components/UserName";
import { CheckCircle2, AlertTriangle } from "lucide-react";

// ONE ROW OF THE DRAFT: the record's value on the PRIVATE side (with where it came from), the listing's value on the
// PUBLIC side. On a PC they sit side by side; on a tablet or phone the record line sits above the field.
const show = (v) => (Array.isArray(v) ? (v.map((x) => (typeof x === "object" ? [x.make, x.model].filter(Boolean).join(" ") : x)).join(", ") || "—") : v === true ? "Yes" : v === false ? "No" : v ?? "—");
const has = (v) => v != null && v !== "" && !(Array.isArray(v) && !v.length);

export function DraftField({ v, row, onView }) {
  const f = useDraftField({ kind: v.kind, id: v.id, row, onSaved: onView });
  const tid = `sales-draft-field-${row.key}`;
  return (
    <div data-testid={tid} className="grid xl:grid-cols-2 gap-x-4 gap-y-1 border-b border-slate-100 py-3">
      <div data-testid={`${tid}-record`} className="text-sm xl:pr-3 xl:border-r xl:border-red-100">
        <div className="text-[11px] font-bold uppercase tracking-wide text-[#B01E28] xl:hidden">Record (private)</div>
        <div className="font-semibold text-slate-800">{row.label}</div>
        <div className="text-slate-600 break-words">{row.record ? show(row.record.value) : <span className="text-slate-400">Written for the listing</span>}</div>
        {row.record && <div data-testid={`${tid}-record-origin`} className="text-[11px] text-slate-500">{row.record.origin_words}</div>}
      </div>
      <div className="space-y-1.5 min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-800 xl:hidden">Listing (public)</div>
        {v.can.prepare ? <DraftInput row={row} f={f} lists={v.lists} testid={`${tid}-input`} /> : <p className="text-sm">{show(row.value)}</p>}
        {has(row.value) && row.origin_words && <div data-testid={`${tid}-origin`} className="text-[11px] text-slate-500">{row.origin_words}</div>}
        {row.key === "price" && has(row.value) && v.listing.call_for_price && <p data-testid={`${tid}-cfp-on`} className="text-xs text-amber-800">Call for Price is on, so this price isn't shown — turn Call for Price off to show it.</p>}
        <FieldStatus f={f} tid={tid} />
        <Confirm v={v} row={row} onView={onView} tid={tid} />
        <SourceUpdated v={v} row={row} onView={onView} tid={tid} />
      </div>
    </div>
  );
}

function FieldStatus({ f, tid }) {
  if (f.conflict) return (
    <div data-testid={`${tid}-conflict`} className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-950 space-y-1.5">
      <p>{f.conflict.message || "Someone changed this since you opened it."}</p>
      <div className="flex gap-2">
        <button type="button" data-testid={`${tid}-take-theirs`} onClick={f.takeTheirs} className="h-11 px-3 rounded-lg border-2 border-amber-700 bg-white font-bold">Keep theirs</button>
        <button type="button" data-testid={`${tid}-keep-mine`} onClick={f.keepMine} className="h-11 px-3 rounded-lg bg-[#1F1F1F] text-white font-bold">Save mine over it</button>
      </div>
    </div>
  );
  const words = f.status === "refused" ? f.error : STATUS_WORDS[f.status];
  return words ? <p data-testid={`${tid}-status`} className={`text-xs ${f.status === "refused" || f.status === "kept" ? "text-amber-800" : "text-slate-500"}`}>{words}</p> : null;
}

function Confirm({ v, row, onView, tid }) {
  const [busy, setBusy] = useState(false);
  if (!row.own_confirm) return null;
  if (row.confirmed) return <p data-testid={`${tid}-confirmed`} className="text-xs text-emerald-800 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Confirmed on its own by <UserName email={row.confirmed.by} /></p>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-amber-800 inline-flex items-center gap-1"><AlertTriangle className="w-4 h-4" />Needs its own confirm{row.void ? ` — ${row.void}` : ""}</span>
      {v.can.confirm && <button type="button" data-testid={`${tid}-confirm`} disabled={busy}
        onClick={async () => { setBusy(true); await draftAct(v, "post", `/confirm/${row.key}`, null, onView); setBusy(false); }}
        className="h-11 px-3 rounded-lg border-2 border-emerald-700 bg-white text-emerald-800 text-sm font-bold disabled:opacity-40">✓ Confirm {row.label}</button>}
    </div>
  );
}

function SourceUpdated({ v, row, onView, tid }) {
  if (!row.source_updated || !v.can.prepare) return null;
  const go = (take) => draftAct(v, "post", `/field/${row.key}/re-review?take=${take}`, null, onView);
  return (
    <div data-testid={`${tid}-source-updated`} className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-950 space-y-1.5">
      <p><b>Source updated — re-review.</b> The record now says {show(row.record?.value)}.</p>
      <div className="flex gap-2">
        <button type="button" data-testid={`${tid}-take-record`} onClick={() => go(true)} className="h-11 px-3 rounded-lg bg-[#1F1F1F] text-white font-bold">Use the record's value</button>
        <button type="button" data-testid={`${tid}-keep-listing`} onClick={() => go(false)} className="h-11 px-3 rounded-lg border-2 border-amber-700 bg-white font-bold">Keep the listing's</button>
      </div>
    </div>
  );
}
